import Button from "components/Elements/Button/Button";
import ManageMfaModal from "./modals/ManageMfaModal";
import useMfa from "hooks/useMfa";
import Form from "components/Reusable/Form/Form";
import useReduxUser from "hooks/useReduxUser";

import { memo, PropsWithChildren, useState } from "react";
import { useContent } from "contexts/contentContext/contentContext";
import { useModal } from "contexts/modalContext/modalContext.js";
import { useAppSelector } from "store/hooks";
import { selectSettings } from "store/selectors/userSelectors";
import { useUser } from "contexts/userContext/userContext";
import type { UserDataT } from "models/models";
import { useToasts } from "react-toast-notifications";
import { UpdateSubUsersMFAApiRequest } from "api/UsersController";

import styles from "./styles.module.scss";

interface MfaSecurityI extends React.HTMLAttributes<HTMLDivElement> {}

const MfaSecurity = memo((props: MfaSecurityI) => {
  const { ...rest } = props;
  const { getTextByKey } = useContent();
  const { addToast } = useToasts();
  const { setModal } = useModal();
  const { toggleMfa } = useMfa();
  const { getUserData } = useUser();
  const { updateSettings } = useReduxUser();
  const userData = getUserData() as UserDataT;
  const [isUpdating, setIsUpdating] = useState(false);

  const settings = useAppSelector(selectSettings);

  const mfaStatus = settings?.EnableMFA;

  const handleOpenMfaSettings = () => {
    const handleCloseModal = (
      e: React.MouseEvent<HTMLDivElement, MouseEvent>
    ) => {
      if (e.target === e.currentTarget) {
        setModal({});
      }
    };

    setModal({
      modal: ManageMfaModal,
      container: ({ children }: PropsWithChildren) => (
        <div className={styles.modalContainer} onClick={handleCloseModal}>
          {children}
        </div>
      ),
      props: { enableMfa: !mfaStatus, cb: toggleMfa },
    });
  };

  const handleSaveToggleValue = async (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>
  ) => {
    setIsUpdating(true);

    // find checkbox element
    const checkbox = (e.target as Element).closest("input[type='checkbox']");

    // if checkbox is not found, return
    if (!checkbox) {
      setIsUpdating(false);
      return;
    }

    // get checked value
    const checked = (checkbox as HTMLInputElement).checked;

    updateSettings({ SubUsersMFAEnabled: checked });

    // call api to update sub users mfa
    const response = await UpdateSubUsersMFAApiRequest({
      EnableMFA: checked,
    });

    if (response.status === "success") {
      if (checked) {
        addToast(getTextByKey("sub-users-mfa-enabled"), {
          appearance: "success",
        });
      } else {
        addToast(getTextByKey("sub-users-mfa-disabled"), {
          appearance: "success",
        });
      }
    } else {
      addToast(getTextByKey("sub-users-mfa-error"), {
        appearance: "error",
      });

      // set checkbox value to previous value
      (checkbox as HTMLInputElement).checked = !checked;

      updateSettings({ SubUsersMFAEnabled: !checked });
    }

    setIsUpdating(false);
  };

  if (getUserData().isTopUser || getUserData().AuthenticationType !== "email") {
    return null;
  }

  return (
    <div className={styles.mfaSecurityWrapper} {...rest}>
      <h1 className={styles.mfaSecurityHeader}>
        {getTextByKey("security-settings-title")}
      </h1>
      {!userData?.IsSubUser ? (
        <p className={styles.mfaSecurityDescription}>
          {getTextByKey("security-settings-description")}
        </p>
      ) : null}
      <p className={styles.mfaSecurityHint}>
        {getTextByKey(
          userData?.IsSubUser
            ? "security-subuser-settings-hint"
            : "security-settings-hint"
        )}
      </p>
      <div className={styles.mfaStatusWrapper}>
        <span>
          {getTextByKey("mfa-status")}:{" "}
          <span className={styles[mfaStatus ? "enabled" : "disabled"]}>
            {getTextByKey(`mfa-${mfaStatus ? "enabled" : "disabled"}-status`)}
          </span>
        </span>
      </div>
      {!userData?.IsSubUser ? (
        <Button
          onClick={handleOpenMfaSettings}
          buttonClass={styles.manageMfaButton}
        >
          {getTextByKey("manage-mfa")}
        </Button>
      ) : null}
      {userData?.subUserRole && mfaStatus ? (
        <div className={styles.subUsersMfaWrapper}>
          <span>{getTextByKey("sub-user-mfa-enabled")}</span>
        </div>
      ) : null}
      {userData?.isTopUser && mfaStatus ? (
        <div
          onClick={handleSaveToggleValue}
          className={`${styles.subUsersMfaWrapper} ${
            isUpdating ? styles.subUsersMfaWrapperDisabled : ""
          }`}
        >
          <Form
            //@ts-ignore
            fields={{
              SubUsersMFAEnabled: {
                fieldKey: "SubUsersMFAEnabled",
                element: "checkbox",
                title: "SubUsersMFAEnabled",
                label: "SubUsersMFAEnabled-notification",
                className: "general-settings",
                tooltip: {
                  text: "SubUsersMFAEnabled-tooltip",
                  icon: "question-circle",
                },
              },
            }}
            defaultValuesObj={{
              SubUsersMFAEnabled: settings?.SubUsersMFAEnabled,
            }}
            submitProps={{
              hidden: true,
            }}
            onSubmit={() => {}}
          />
        </div>
      ) : null}
    </div>
  );
});

export default MfaSecurity;
